import { useEffect, useState, useCallback } from "react";
import OwnerLayout from "../layout/OwnerLayout";
import FraudCard from "../components/FraudCard";
import { API_BASE_URL } from "../../config/api";
import { authHeader } from "../../utils/authHeader";
import Pagination from "../../components/Pagination";
import FRAUD_TYPES from "../../config/fraudTypes";

const TABS = [
  { key: "pending",      label: "⏳ Pending",      color: "#d97706", bg: "#fef3c7" },
  { key: "approved",     label: "✅ Approved",     color: "#16a34a", bg: "#dcfce7" },
  { key: "rejected",     label: "❌ Rejected",     color: "#dc2626", bg: "#fee2e2" },
  { key: "under_review", label: "🔍 Under Review", color: "#0891b2", bg: "#ecfeff" },
  { key: "all",          label: "📋 All",          color: "#4318FF", bg: "#ede9fe" },
];

const RegisteredFrauds = () => {
  const [frauds,     setFrauds]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [exporting,  setExporting]  = useState(false);
  const [canExportCSV, setCanExportCSV] = useState(false);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [tabCounts,  setTabCounts]  = useState({});
  const LIMIT = 10;

  const [tab,       setTab]       = useState("pending");
  const [search,    setSearch]    = useState("");
  const [fraudType, setFraudType] = useState("all");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");

  const fetchFrauds = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage, limit: LIMIT,
        ...(tab !== "all"       && { status: tab }),
        ...(search              && { search }),
        ...(fraudType !== "all" && { fraudType }),
        ...(dateFrom            && { dateFrom }),
        ...(dateTo              && { dateTo }),
      });
      const res  = await fetch(`${API_BASE_URL}/api/fraud/my-frauds?${params}`, {
        headers: { Authorization: authHeader().Authorization },
      });
      const data = await res.json();
      if (res.ok) {
        setFrauds(data.data || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setPage(data.currentPage || 1);
      }
    } catch (err) { console.error("Failed to load frauds", err); }
    setLoading(false);
  }, [tab, search, fraudType, dateFrom, dateTo]);

  const fetchCounts = useCallback(async () => {
    try {
      const statuses = ["pending", "approved", "rejected", "under_review"];
      const counts = {};
      await Promise.all(statuses.map(async (s) => {
        const res  = await fetch(`${API_BASE_URL}/api/fraud/my-frauds?status=${s}&limit=1&page=1`, {
          headers: { Authorization: authHeader().Authorization },
        });
        const data = await res.json();
        counts[s] = data.total || 0;
      }));
      setTabCounts(counts);
    } catch {}
  }, []);

  useEffect(() => { setPage(1); fetchFrauds(1); }, [fetchFrauds]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/subscription/my`, { headers: authHeader() })
      .then(response => response.ok ? response.json() : null)
      .then(data => setCanExportCSV(data?.planFeatures?.canExportCSV === true))
      .catch(() => setCanExportCSV(false));
  }, []);

  const handleReset = () => {
    setSearch(""); setFraudType("all"); setDateFrom(""); setDateTo("");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        ...(tab !== "all"       && { status: tab }),
        ...(fraudType !== "all" && { fraudType }),
        ...(dateFrom            && { dateFrom }),
        ...(dateTo              && { dateTo }),
      });
      const res = await fetch(`${API_BASE_URL}/api/fraud/export-csv?${params}`, { headers: authHeader() });
      if (!res.ok) { alert("Export failed"); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `fraud-reports-${tab}-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Export failed"); }
    setExporting(false);
  };

  const currentTab = TABS.find(t => t.key === tab);
  const activeFilters = [search, fraudType !== "all", dateFrom, dateTo].filter(Boolean).length;

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">My Reports</p>
        <h2 className="company-name">
          Registered Fraud Cases
          {total > 0 && <span style={{ fontSize: 14, fontWeight: 500, color: "#a3aed0", marginLeft: 10 }}>({total})</span>}
        </h2>
      </div>

      <div className="owner-dashboard-wrapper">

        {/* Tab count cards */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          {TABS.filter(t => t.key !== "all").map(t => (
            <div key={t.key} onClick={() => { setTab(t.key); setPage(1); handleReset(); }}
              style={{ background: t.bg, borderRadius: 12, padding: "10px 16px", minWidth: 80, textAlign: "center",
                cursor: "pointer", border: `2px solid ${tab === t.key ? t.color : "transparent"}`, flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.color }}>{tabCounts[t.key] ?? "—"}</div>
              <div style={{ fontSize: 11, color: t.color, fontWeight: 600 }}>
                {t.key === "under_review" ? "Review" : t.key.charAt(0).toUpperCase() + t.key.slice(1)}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "2px solid #e0e5f2", background: "#fff", borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setPage(1); handleReset(); }}
              style={{
                flex: 1, padding: "10px 8px", border: "none", cursor: "pointer", background: "none",
                fontSize: 12, fontWeight: 700,
                color: tab === t.key ? t.color : "#a3aed0",
                borderBottom: tab === t.key ? `3px solid ${t.color}` : "3px solid transparent",
                marginBottom: "-2px", transition: "all 0.2s",
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", marginBottom: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#a3aed0" }}>🔍</span>
              <input type="text" placeholder="Search name, phone, DL…" value={search}
                onChange={e => setSearch(e.target.value)} style={inputStyle} />
            </div>
            <select value={fraudType} onChange={e => setFraudType(e.target.value)} style={selectStyle}>
              <option value="all">All Types</option>
              {FRAUD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <label style={{ fontSize: 12, color: "#a3aed0" }}>From:</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...selectStyle, width: 140 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <label style={{ fontSize: 12, color: "#a3aed0" }}>To:</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...selectStyle, width: 140 }} />
            </div>
            {activeFilters > 0 && (
              <button onClick={handleReset} style={resetBtnStyle}>✕ Clear ({activeFilters})</button>
            )}
            {canExportCSV && <button onClick={handleExport} disabled={exporting || total === 0}
              style={{ marginLeft: "auto", height: 36, padding: "0 14px",
                background: exporting ? "#a3aed0" : "#16a34a", color: "#fff",
                border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: exporting || total === 0 ? "not-allowed" : "pointer" }}>
              📥 {exporting ? "Exporting…" : `Export CSV (${total})`}
            </button>}
          </div>
        </div>

        {/* Results */}
        <div className="owner-fraud-container">
          {loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>Loading…
            </div>
          ) : frauds.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{currentTab?.label.split(" ")[0]}</div>
              <p style={{ fontWeight: 600, color: "#1b2559" }}>No {tab === "all" ? "" : tab} reports found</p>
              {tab === "rejected" && (
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Rejected reports can be edited and resubmitted</p>
              )}
              {activeFilters > 0 && <button onClick={handleReset} style={{ ...resetBtnStyle, marginTop: 12 }}>Clear filters</button>}
            </div>
          ) : (
            <>
              {/* Helper message for rejected tab */}
              {tab === "rejected" && (
                <div style={{ background: "#fff5f5", border: "1.5px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#dc2626" }}>
                  ✏️ <b>Rejected reports can be edited</b> — click the Edit button to fix and resubmit for admin review.
                </div>
              )}
              {tab === "pending" && (
                <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#92400e" }}>
                  ⏳ <b>Pending reports</b> are waiting for admin approval. You can edit or delete them before approval.
                </div>
              )}

              <div className="owner-fraud-list">
                {frauds.map(fraud => (
                  <FraudCard
                    key={fraud._id}
                    fullName={fraud.fullName}
                    fraudType={fraud.fraudType}
                    dlNo={fraud.drivingLicense}
                    phone={fraud.phoneNumbers?.join(", ")}
                    status={fraud.status}
                    date={fraud.fraudDate || fraud.createdAt}
                    location={`${fraud.fraudCity || ""}${fraud.fraudArea ? " - " + fraud.fraudArea : ""}`}
                    fraud={fraud}
                    onRefresh={() => { fetchFrauds(page); fetchCounts(); }}
                  />
                ))}
              </div>
              <Pagination currentPage={page} totalPages={totalPages} total={total} limit={LIMIT}
                onPageChange={(p) => { setPage(p); fetchFrauds(p); }} />
            </>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
};

const inputStyle = { width: "100%", height: 38, padding: "0 12px 0 32px", borderRadius: 8, border: "1px solid #e0e5f2", fontSize: 13, outline: "none", background: "#f9faff", color: "#1b2559" };
const selectStyle = { height: 38, padding: "0 12px", borderRadius: 8, border: "1px solid #e0e5f2", fontSize: 13, outline: "none", background: "#f9faff", color: "#1b2559", cursor: "pointer" };
const resetBtnStyle = { height: 36, padding: "0 12px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" };

export default RegisteredFrauds;